"use client";

import { useQuery } from "@tanstack/react-query";
import { BarChart3, PieChart } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";

import { AppModelTable } from "@/components/ranking/app-model-table";
import { FilterBar } from "@/components/ranking/filter-bar";
import { KPICards } from "@/components/ranking/kpi-cards";
import { LeaderboardTable } from "@/components/ranking/leaderboard-table";
import { MarketShareChart } from "@/components/ranking/market-share-chart";
import { PopularityTrendChart } from "@/components/ranking/popularity-trend-chart";
import { TrendingChart } from "@/components/ranking/trending-chart";
import { PageHeader, PageHeaderHeading } from "@/components/shared/page-header";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/lib/components/card";
import { useAppConfig } from "@/lib/config";

interface LeaderboardItem {
	model: string;
	provider: string;
	requestCount: number;
	appCount: number;
	avgLatency: number;
	successRate: number;
	costPer1kTokens: number;
	trendingPercent: number | null;
}

export function RankingDashboard() {
	const { apiUrl } = useAppConfig();
	const searchParams = useSearchParams();
	const period = searchParams.get("period") || "30d";
	const provider = searchParams.get("provider");
	const sortBy = searchParams.get("sortBy") || "requests";

	// Fetch leaderboard data
	const { data: leaderboardData, isLoading: leaderboardLoading } = useQuery({
		queryKey: ["model-leaderboard", period, provider, sortBy],
		queryFn: async () => {
			const params = new URLSearchParams({
				period,
				sortBy,
				limit: "20",
			});
			if (provider) {
				params.append("provider", provider);
			}

			const response = await fetch(
				`${apiUrl}/analytics/models/leaderboard?${params}`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch leaderboard");
			}
			return await (response.json() as Promise<{
				leaderboard: LeaderboardItem[];
				total: number;
				period: string;
			}>);
		},
	});

	// Fetch market share data
	const { data: marketShareData, isLoading: marketShareLoading } = useQuery({
		queryKey: ["market-share", period],
		queryFn: async () => {
			const response = await fetch(
				`${apiUrl}/analytics/charts/market-share?period=${period}&limit=10`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch market share");
			}
			return await (response.json() as Promise<{
				data: Array<{ name: string; value: number; percentage: number }>;
			}>);
		},
	});

	// Fetch popularity trend data
	const { data: trendData, isLoading: trendLoading } = useQuery({
		queryKey: ["popularity-trend", period],
		queryFn: async () => {
			const response = await fetch(
				`${apiUrl}/analytics/charts/popularity-trend?period=${(period as any).replace("all-time", "30d")}&topModels=5`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch trend");
			}
			return await (response.json() as Promise<{
				data: Array<{ date: string; models: Record<string, number> }>;
			}>);
		},
	});

	// Fetch app usage data
	const { data: appUsageData, isLoading: appUsageLoading } = useQuery({
		queryKey: ["app-usage", period],
		queryFn: async () => {
			const response = await fetch(
				`${apiUrl}/analytics/apps/usage?period=${period}&minRequests=1&limit=10`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch app usage");
			}
			return await (response.json() as Promise<{
				data: Array<{
					app: string;
					model: string;
					requestCount: number;
					percentOfApp: number;
				}>;
				apps: string[];
				models: string[];
			}>);
		},
	});

	// Fetch trending data
	const { data: trendingData, isLoading: trendingLoading } = useQuery({
		queryKey: ["trending", period],
		queryFn: async () => {
			const response = await fetch(
				`${apiUrl}/analytics/models/trending?limit=20`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch trending");
			}
			return await (response.json() as Promise<{
				trending: Array<{
					model: string;
					provider: string;
					currentWeek: number;
					previousWeek: number;
					growthPercent: number;
				}>;
			}>);
		},
	});

	// Fetch app trending data
	const { data: appTrendingData, isLoading: appTrendingLoading } = useQuery({
		queryKey: ["app-trending", period],
		queryFn: async () => {
			const response = await fetch(
				`${apiUrl}/analytics/apps/trending?period=${period}&limit=10`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch app trending");
			}
			return await (response.json() as Promise<{
				trending: Array<{
					app: string;
					currentWeek: number;
					previousWeek: number;
					growthPercent: number;
				}>;
			}>);
		},
	});

	// Compute KPI values
	const kpiValues = useMemo(() => {
		const leaderboard = leaderboardData?.leaderboard || [];
		const trending = trendingData?.trending || [];

		const totalRequests = leaderboard.reduce(
			(sum, item) => sum + item.requestCount,
			0,
		);
		const mostPopular = leaderboard[0] || { model: "N/A", provider: "N/A" };
		const trendingModel = trending[0] || { model: "N/A", growthPercent: 0 };

		// Get top app from app usage data
		const appUsageMap = new Map<string, number>();
		appUsageData?.data.forEach((item) => {
			appUsageMap.set(
				item.app,
				(appUsageMap.get(item.app) || 0) + item.requestCount,
			);
		});
		const topApp =
			Array.from(appUsageMap.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ||
			"N/A";

		return {
			totalRequests,
			mostPopularModel: mostPopular.model,
			mostPopularProvider: mostPopular.provider,
			trendingModel: trendingModel.model,
			trendingGrowth: trendingModel.growthPercent,
			topApp,
		};
	}, [leaderboardData, trendingData, appUsageData]);

	// Get unique providers for filter
	const providers = useMemo(() => {
		const uniqueProviders = new Set<string>();
		leaderboardData?.leaderboard.forEach((item) => {
			uniqueProviders.add(item.provider);
		});
		return Array.from(uniqueProviders).sort();
	}, [leaderboardData]);

	const isLoading =
		leaderboardLoading ||
		marketShareLoading ||
		trendLoading ||
		appUsageLoading ||
		trendingLoading ||
		appTrendingLoading;

	return (
		<div className="space-y-8">
			<PageHeader>
				<PageHeaderHeading>Model Rankings & Analytics</PageHeaderHeading>
				<p className="text-muted-foreground">
					Explore live analytics on AI model usage, performance, and trends
					across all providers.
				</p>
			</PageHeader>

			{/* Filters */}
			<FilterBar providers={providers} />

			{/* KPI Cards */}
			{!isLoading && (
				<KPICards
					totalRequests={kpiValues.totalRequests}
					mostPopularModel={kpiValues.mostPopularModel}
					mostPopularProvider={kpiValues.mostPopularProvider}
					trendingModel={kpiValues.trendingModel}
					trendingGrowth={kpiValues.trendingGrowth}
					topApp={kpiValues.topApp}
				/>
			)}

			{/* Charts Row 1 */}
			<div className="grid gap-6 lg:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<PieChart className="h-4 w-4" />
							Market Share
						</CardTitle>
						<CardDescription>
							Request distribution across top models
						</CardDescription>
					</CardHeader>
					<CardContent>
						<MarketShareChart
							data={marketShareData?.data || []}
							isLoading={marketShareLoading}
						/>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<BarChart3 className="h-4 w-4" />
							Trending Models
						</CardTitle>
						<CardDescription>
							Week-over-week growth rates (top 10)
						</CardDescription>
					</CardHeader>
					<CardContent>
						<TrendingChart
							data={trendingData?.trending || []}
							isLoading={trendingLoading}
						/>
					</CardContent>
				</Card>
			</div>

			{/* Popularity Trend */}
			<Card>
				<CardHeader>
					<CardTitle>Popularity Trend</CardTitle>
					<CardDescription>
						Request volume over the last 30 days (top 5 models)
					</CardDescription>
				</CardHeader>
				<CardContent>
					<PopularityTrendChart
						data={trendData?.data || []}
						isLoading={trendLoading}
					/>
				</CardContent>
			</Card>

			{/* Leaderboard */}
			<Card>
				<CardHeader>
					<CardTitle>Model Leaderboard</CardTitle>
					<CardDescription>
						Complete ranking with performance metrics
					</CardDescription>
				</CardHeader>
				<CardContent>
					{leaderboardLoading ? (
						<div className="flex h-64 items-center justify-center">
							<div className="text-sm text-muted-foreground">
								Loading leaderboard...
							</div>
						</div>
					) : (
						<LeaderboardTable data={leaderboardData?.leaderboard || []} />
					)}
				</CardContent>
			</Card>

			{/* App-to-Model Usage Table */}
			<Card>
				<CardHeader>
					<CardTitle>App-to-Model Usage</CardTitle>
					<CardDescription>
						Top apps and their model usage with weekly trends
					</CardDescription>
				</CardHeader>
				<CardContent>
					<AppModelTable
						data={appUsageData?.data || []}
						trendData={appTrendingData?.trending || []}
						isLoading={appUsageLoading || appTrendingLoading}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
